/**
 * El cliente del screening — `POST /job-apply/api/screening` y
 * `POST /job-apply/api/screening/regenerate`.
 *
 * Una clase de error y no un `Error(mensaje)`, por la misma razón que en
 * `threadsClient`: estas dos llamadas fallan de siete maneras que la pantalla
 * tiene que tratar distinto. `no_membership` se resuelve solo (se da de alta el
 * canal y se reintenta), `invalid_screening` devuelve a la persona al paso que
 * falló, `regeneration_limit` apaga un botón y `missing_screening_answers`
 * manda a rehacer el cuestionario. Sin el `code` no se puede escribir ninguna
 * de esas frases.
 */
import { messages } from '~/i18n';
import { apiFetch } from '~/lib/api';
import { normalizeScoreDetail, type CVScoreDetail } from '../lib/cvScore';
import type { ScreeningAnswers, ScreeningMetadata } from '../lib/screening';

export interface ScreeningCVResult {
  cv_id: string;
  cv_text: string;
  summary: string;
  skills: string[];
  /**
   * Sólo en la creación: false si ya había otra hoja de vida principal y ésta
   * no vino a reemplazar a la que se estaba revisando. En ese caso la revisión
   * pregunta con cuál postular.
   */
  is_primary?: boolean;
  regenerations_left: number;
  /**
   * La nota de esta hoja de vida, recalculada en cada escritura (crear y
   * rehacer). `null` NO es cero: es que el modelo no alcanzó a calificar y el
   * CV se guardó igual — puntuar nunca tumba el camino que lo llama. La
   * pantalla que reciba null esconde el puntaje, no dibuja un 0.
   *
   * Opcional además de anulable: un borrador guardado en sessionStorage ANTES
   * de que el puntaje existiera se restaura sin el campo, y eso no puede ser un
   * error de tipos en la pantalla que lo lee.
   */
  score?: CVScoreDetail | null;
}

export interface ScreeningIssue {
  /** Notación de punto lista para anclar: 'availability.start_when'. */
  path: string;
  message: string;
}

export class ScreeningError extends Error {
  readonly code: string;
  readonly status: number;
  readonly issues: ScreeningIssue[];
  /** Sólo en el 429: lo manda el servidor y ya viene en cero. */
  readonly regenerationsLeft?: number;

  constructor(
    message: string,
    options: {
      code: string;
      status: number;
      issues?: ScreeningIssue[];
      regenerationsLeft?: number;
    },
  ) {
    super(message);
    this.name = 'ScreeningError';
    this.code = options.code;
    this.status = options.status;
    this.issues = options.issues ?? [];
    this.regenerationsLeft = options.regenerationsLeft;
  }
}

/**
 * Lo que se le dice a la persona por cada código del contrato
 * (`screening.apiErrors`). Si el código no es conocido, se muestra el `error`
 * del servidor tal cual.
 */
function messageFor(code: string, fallback?: string): string {
  const text: Record<string, string> = messages().screening.apiErrors;
  return text[code] ?? fallback ?? text.unknown;
}

async function postJSON(path: string, body: unknown): Promise<ScreeningCVResult> {
  let res: Response;
  try {
    res = await apiFetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ScreeningError(messageFor('network'), { code: 'network', status: 0 });
  }

  const payload = (await res.json().catch(() => null)) as
    | (Partial<Omit<ScreeningCVResult, 'score'>> & {
        error?: string;
        code?: string;
        issues?: ScreeningIssue[];
        regenerations_left?: number;
        cv_score?: number | null;
        cv_score_detail?: unknown;
      })
    | null;

  if (!res.ok) {
    const code = payload?.code ?? (res.status === 401 ? 'unauthorized' : 'unknown');
    throw new ScreeningError(messageFor(code, payload?.error), {
      code,
      status: res.status,
      issues: payload?.issues ?? [],
      regenerationsLeft: payload?.regenerations_left,
    });
  }

  return {
    cv_id: String(payload?.cv_id ?? ''),
    cv_text: payload?.cv_text ?? '',
    summary: payload?.summary ?? '',
    skills: payload?.skills ?? [],
    is_primary: payload?.is_primary,
    regenerations_left: payload?.regenerations_left ?? 0,
    // `cv_score_detail` trae el objeto entero; `cv_score` a secas es el número
    // suelto que ya viene adentro. Se lee el detalle: sin los huecos, la nota
    // no le sirve de nada a nadie.
    score: normalizeScoreDetail((payload as any)?.cv_score_detail),
  };
}

/**
 * `answer_quality` e `input_mode` viajan en la RAÍZ del cuerpo, al lado de
 * `answers` y no adentro. El servidor acepta las dos formas y las junta, pero
 * adentro tendrían que pasar por el `strictObject` del cuestionario, que es el
 * mismo con el que `/regenerate` relee las respuestas guardadas: una llave de
 * más ahí no da un 400 hoy, da un CV irregenerable mañana.
 *
 * Un mapa vacío se OMITE. Quien contestó todo escribiendo no genera analítica
 * de voz, y `{}` en la base es una fila que después hay que aprender a ignorar.
 */
export function createScreeningCV(
  answers: ScreeningAnswers,
  metadata?: ScreeningMetadata,
  replacesCvId?: string | null,
): Promise<ScreeningCVResult> {
  // El sobre `answers` es obligatorio: el cuestionario pelado da 400.
  const quality = metadata?.answer_quality ?? {};
  const mode = metadata?.input_mode ?? {};
  return postJSON('/job-apply/api/screening', {
    answers,
    ...(Object.keys(quality).length ? { answer_quality: quality } : {}),
    ...(Object.keys(mode).length ? { input_mode: mode } : {}),
    // La hoja de vida que se estaba revisando al volver al cuestionario: la
    // nueva la REEMPLAZA y se queda con su lugar de "la de postular". Sin esto
    // el servidor crea una paralela, secundaria, y la persona ve subir un
    // puntaje que no usa nadie.
    ...(replacesCvId ? { replaces_cv_id: replacesCvId } : {}),
  });
}

/**
 * `GET /job-apply/api/screening/answers` — las respuestas con que se escribió
 * esa hoja de vida, para volver al cuestionario con lo ya contestado. `null` si
 * no hay (CV subido como archivo, o anterior al cuestionario) o si falla: el
 * cuestionario arranca vacío como siempre, que no es peor que antes.
 */
export async function fetchScreeningAnswers(cvId: string): Promise<unknown | null> {
  try {
    const res = await apiFetch(`/job-apply/api/screening/answers?cv_id=${encodeURIComponent(cvId)}`);
    if (!res.ok) return null;
    const body = (await res.json()) as { answers?: unknown };
    return body.answers ?? null;
  } catch {
    return null;
  }
}

/**
 * `POST /job-apply/api/cv/primary` — "Usar esta hoja de vida". Lanza
 * `ScreeningError` como las otras dos para que la revisión pinte el error igual.
 */
export async function makeCVPrimary(cvId: string): Promise<void> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/cv/primary', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ cv_id: cvId }),
    });
  } catch {
    throw new ScreeningError(messageFor('network'), { code: 'network', status: 0 });
  }
  if (res.ok) return;

  const payload = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  const code = payload?.code ?? (res.status === 401 ? 'unauthorized' : 'set_primary_failed');
  throw new ScreeningError(messageFor(code, payload?.error), { code, status: res.status });
}

export function regenerateScreeningCV(
  cvId: string,
  feedback?: string,
): Promise<ScreeningCVResult> {
  const trimmed = feedback?.trim();
  return postJSON('/job-apply/api/screening/regenerate', {
    cv_id: cvId,
    ...(trimmed ? { feedback: trimmed } : {}),
  });
}
